import { BusinessObjectBase } from './base';
import { userTaskHistorySchema } from '@schemas/business-objects/user-task-history';
import type { Tenant } from './tenant';
import type { UserTask } from './user-task';
import type { HistoryData, Id, UserTaskHistoryEventType } from '../base-types';

export interface UserTaskHistoryData {
  readonly id: Id;
  readonly eventType: UserTaskHistoryEventType;
  readonly data?: HistoryData;
  readonly timestamp?: Date;
  readonly userTaskId: Id;
  readonly userTask?: UserTask;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface UserTaskHistory extends Readonly<UserTaskHistoryData> {}
type UserTaskHistoryCreateWithChildrenData = Partial<UserTaskHistoryData>;

export class UserTaskHistory extends BusinessObjectBase {
  static readonly entityName = 'UserTaskHistory' as const;
  static readonly plural = 'user-task-histories';
  static schema = userTaskHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<UserTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new UserTaskHistory(data));
  }

  static async findOne(filter?: any): Promise<UserTaskHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new UserTaskHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<UserTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new UserTaskHistory(data);
  }

  static async create(
    data: Omit<UserTaskHistoryData, 'id'>,
  ): Promise<UserTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new UserTaskHistory(result);
  }

  static async createMany(
    data: Omit<UserTaskHistoryData, 'id'>[],
  ): Promise<UserTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new UserTaskHistory(item));
  }

  static async createWithChildren(
    data: UserTaskHistoryCreateWithChildrenData,
  ): Promise<UserTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new UserTaskHistory(result);
  }

  static async createManyWithChildren(
    data: UserTaskHistoryCreateWithChildrenData[],
  ): Promise<UserTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new UserTaskHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<UserTaskHistoryData>,
  ): Promise<UserTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new UserTaskHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
